// src/frontend/src/components/Common/PersistentUpgradeSidebar.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';

interface UserPlan {
  planType: string;
  creditsUsed: number;
  creditsTotal: number;
  isTrialActive: boolean;
  trialDaysLeft: number;
}

const PersistentUpgradeSidebar: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPlan();
    }
    
    // Check if dismissed in this session
    const dismissed = sessionStorage.getItem('upgradeSidebarDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, [isAuthenticated]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('upgradeSidebarDismissed', 'true');
  };

  // Don't show if not authenticated, dismissed, or already on premium plan
  if (!isAuthenticated || isDismissed || !userPlan) return null;
  
  // Only show for trial users or Solo Core users
  const shouldShow = userPlan.isTrialActive || userPlan.planType === 'solo_core';
  if (!shouldShow) return null;

  const creditsPercentageUsed = (userPlan.creditsUsed / userPlan.creditsTotal) * 100;
  const isUrgent = userPlan.isTrialActive && userPlan.trialDaysLeft <= 3;
  const isLowCredits = creditsPercentageUsed >= 80;

  return (
    <div className={`fixed right-4 top-24 w-80 rounded-xl shadow-xl z-50 transition-all duration-300 ${
      isUrgent ? 'bg-gradient-to-br from-red-500 to-pink-600' : 
      isLowCredits ? 'bg-gradient-to-br from-orange-500 to-red-500' :
      'bg-gradient-to-br from-blue-600 to-purple-600'
    } text-white`}>
      <div className="p-6">
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/70 hover:text-white"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Urgency indicator */}
        {isUrgent && (
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-semibold">ACTION REQUIRED</span>
          </div>
        )}

        {/* Main content based on situation */}
        {userPlan.isTrialActive ? (
          <div>
            <h3 className="text-lg font-bold mb-2">
              {isUrgent ? `Trial expires in ${userPlan.trialDaysLeft} day${userPlan.trialDaysLeft === 1 ? '' : 's'}!` : 'Upgrade Your Trial'}
            </h3>
            <p className="text-white/90 text-sm mb-4">
              {isUrgent 
                ? 'Don\'t lose access to your coaching progress. Subscribe now to continue!' 
                : 'Get 3x more coaching sessions + real-time tips with Solo Pro'
              }
            </p>
            
            {/* Quick stats */}
            <div className="bg-white/20 rounded-lg p-3 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Sessions used:</span>
                <span className="font-semibold">{userPlan.creditsUsed}/{userPlan.creditsTotal}</span>
              </div>
              <div className="w-full bg-white/30 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${creditsPercentageUsed}%` }}
                ></div>
              </div>
              <div className="text-xs text-white/80 mt-1">
                {userPlan.creditsTotal - userPlan.creditsUsed} sessions remaining
              </div>
            </div>

            {/* Upgrade buttons */}
            <div className="space-y-2">
              <button
                onClick={() => createCheckoutSession('solo_pro', 'monthly')}
                disabled={isCreatingCheckout}
                className="w-full bg-white text-blue-600 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {isCreatingCheckout ? 'Setting up...' : 'Upgrade to Pro - $39/month'}
              </button>
              <button
                onClick={() => createCheckoutSession('solo_core', 'monthly')}
                disabled={isCreatingCheckout}
                className="w-full bg-white/20 backdrop-blur-sm py-2 px-4 rounded-lg font-medium hover:bg-white/30 transition-colors text-sm disabled:opacity-50"
              >
                {isCreatingCheckout ? 'Setting up...' : 'Start with Core - $19/month'}
              </button>
            </div>
          </div>
        ) : userPlan.planType === 'solo_core' ? (
          <div>
            <h3 className="text-lg font-bold mb-2">
              {isLowCredits ? 'Running Low on Sessions!' : 'Upgrade to Pro'}
            </h3>
            <p className="text-white/90 text-sm mb-4">
              Get 3x more coaching sessions + real-time coaching tips that help improve your responses as you type!
            </p>
            
            {/* Current vs Pro comparison */}
            <div className="bg-white/20 rounded-lg p-3 mb-4">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/80">Current (Core):</span>
                  <span className="font-semibold">20 sessions/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">With Pro:</span>
                  <span className="font-semibold text-yellow-300">60 sessions/month + real-time tips</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => createCheckoutSession('solo_pro', 'monthly')}
              disabled={isCreatingCheckout}
              className="w-full bg-white text-blue-600 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {isCreatingCheckout ? 'Setting up...' : 'Upgrade to Pro - $39/month'}
            </button>
          </div>
        ) : null}

        {/* Bottom link */}
        <div className="mt-4 pt-3 border-t border-white/20">
          <Link 
            to="/pricing" 
            className="text-white/80 hover:text-white text-sm underline"
          >
            View all pricing options →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PersistentUpgradeSidebar;